<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <div class="min-h-screen bg-[var(--color-bg)] text-[var(--color-text)] flex flex-col">
    <div class="flex-1 container-padding py-4 pb-24">
      <!-- Filter -->
      <div class="flex gap-2 flex-wrap mb-4">
        <button 
          v-for="filter in filterList" 
          :key="filter.id"
          @click="activeFilter = filter.id"
          :class="['px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors border shadow-sm', 
            activeFilter === filter.id 
              ? 'bg-[var(--color-primary-600)] text-white border-[var(--color-primary-600)]' 
              : 'bg-[var(--color-primary-100)] text-[var(--color-text)] border-[var(--color-primary-300)] hover:bg-[var(--color-primary-200)]'
          ]"
        >
          {{ filter.label }}
        </button>
      </div>

      <div v-if="loading" class="flex justify-center py-8">
        <div class="animate-spin w-8 h-8 border-4 border-[var(--color-primary-500)] border-t-transparent rounded-full"></div>
      </div>
      
      <div v-else-if="filteredActivities.length === 0" class="text-center py-10 opacity-50">
        <p>{{ t('common.tableList.noEntries') }}</p>
      </div>
      
      <div v-else class="space-y-6">
        <div v-for="(group, gIndex) in groupedActivities" :key="gIndex">
          <h3 class="text-xs font-bold text-[var(--color-text)] opacity-50 uppercase tracking-wider mb-2 ml-1">
            {{ group.title }}
          </h3>
          <div class="space-y-3">
            <ActivityCard
              v-for="(item, i) in group.items" 
              :key="i" 
              :activity="item"
              @click="handleActivityClick(item)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useUiStore } from '@/stores/ui.store.js'
import { useActivities } from '@/utils/home/useActivities'
import ActivityCard from '@/components/ui/cards/ActivityCard.vue'

const { t } = useI18n()
const uiStore = useUiStore()
const router = useRouter()
const { activities, refresh, loading } = useActivities()

const activeFilter = ref('all')

//filter types
const filterList = computed(() => [
  { id: 'all', label: t('views.groups.allGroups').replace(t('bottomMenu.groups'), '').trim() || 'All' },
  { id: 'goat', label: t('bottomMenu.goats') },
  { id: 'group', label: t('bottomMenu.groups') },
  { id: 'weight', label: t('views.session.options.weight') },
  { id: 'temperature', label: t('views.session.options.temperature') },
  { id: 'famacha', label: 'FAMACHA' },
  { id: 'health', label: t('views.selectedGoat.modules.health') },
])

const filteredActivities = computed(() => {
  if (activeFilter.value === 'all') return activities.value
  if (activeFilter.value === 'health') return activities.value.filter(a => ['treatment', 'wound'].includes(a.type))
  return activities.value.filter(a => a.type === activeFilter.value)
})

const groupedActivities = computed(() => {
  const groups = {
    today: [],
    yesterday: [],
    week: [],
    month: [],
    older: []
  }

  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  const lastWeek = new Date(today)
  lastWeek.setDate(lastWeek.getDate() - 7)
  const lastMonth = new Date(today)
  lastMonth.setMonth(lastMonth.getMonth() - 1)

  filteredActivities.value.forEach(activity => {
    const date = new Date(activity.timestamp)
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())

    if (dateOnly.getTime() === today.getTime()) {
      groups.today.push(activity)
    } else if (dateOnly.getTime() === yesterday.getTime()) {
      groups.yesterday.push(activity)
    } else if (dateOnly > lastWeek) {
      groups.week.push(activity)
    } else if (dateOnly > lastMonth) {
      groups.month.push(activity)
    } else {
      groups.older.push(activity)
    }
  })

  const result = []
  if (groups.today.length) result.push({ title: t('views.home.overview.calendar.today'), items: groups.today })
  if (groups.yesterday.length) result.push({ title: t('views.selectedGoat.modules.yesterday'), items: groups.yesterday })
  if (groups.week.length) result.push({ title: t('views.home.overview.calendar.week'), items: groups.week })
  if (groups.month.length) result.push({ title: t('views.home.overview.calendar.month'), items: groups.month })
  if (groups.older.length) result.push({ title: t('views.selectedGoat.modules.previous'), items: groups.older })

  return result
})

function handleActivityClick(activity) {
  if (!activity) return

  switch (activity.type) {
    case 'goat':
      router.push({ name: 'GoatDetail', params: { id: activity.id } })
      break
    case 'group':
      router.push({ name: 'GroupDetail', params: { groupId: activity.id } })
      break
    case 'weight':
      router.push({ name: 'Weight', params: { id: activity.goatId } })
      break
    case 'temperature':
      router.push({ name: 'Temperature', params: { id: activity.goatId } })
      break
    case 'famacha':
      router.push({ name: 'Famacha', params: { id: activity.goatId } })
      break
    case 'treatment':
      router.push({ name: 'Treatments', params: { id: activity.goatId } })
      break
    case 'wound':
      router.push({ name: 'Wounds', params: { id: activity.goatId } })
      break
  }
}

onMounted(async () => {
  uiStore.setHeader({
    title: t('views.activities.title'),
    showBack: true
  })
  await refresh()
})
</script>
